import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Users, Calendar, RefreshCw } from "lucide-react";

// ── Helpers ───────────────────────────────────────────────────────────────────

const PROJECT_COLORS = [
  { bg: "bg-violet-500",  text: "text-white", light: "bg-violet-100 text-violet-700" },
  { bg: "bg-emerald-500", text: "text-white", light: "bg-emerald-100 text-emerald-700" },
  { bg: "bg-amber-400",   text: "text-white", light: "bg-amber-100 text-amber-700" },
  { bg: "bg-sky-500",     text: "text-white", light: "bg-sky-100 text-sky-700" },
  { bg: "bg-rose-500",    text: "text-white", light: "bg-rose-100 text-rose-700" },
  { bg: "bg-indigo-500",  text: "text-white", light: "bg-indigo-100 text-indigo-700" },
  { bg: "bg-pink-500",    text: "text-white", light: "bg-pink-100 text-pink-700" },
  { bg: "bg-teal-500",    text: "text-white", light: "bg-teal-100 text-teal-700" },
  { bg: "bg-orange-500",  text: "text-white", light: "bg-orange-100 text-orange-700" },
  { bg: "bg-cyan-500",    text: "text-white", light: "bg-cyan-100 text-cyan-700" },
];

function colorForProject(id) {
  if (!id) return PROJECT_COLORS[0];
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) | 0;
  return PROJECT_COLORS[Math.abs(h) % PROJECT_COLORS.length];
}

function startOfWeek(d) {
  const dt = new Date(d);
  const day = dt.getDay();
  dt.setDate(dt.getDate() - day + (day === 0 ? -6 : 1)); // Mon
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function addDays(d, n) {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + n);
  return dt;
}

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max);
}

const WEEKS = 5; // show 5 weeks = 35 days
const DAY_W = 36; // px per day cell

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAY_NAMES = ["Mo","Tu","We","Th","Fr","Sa","Su"];

// ── Avatar ────────────────────────────────────────────────────────────────────

const AVATAR_COLORS = ["bg-violet-500","bg-emerald-500","bg-amber-500","bg-sky-500","bg-rose-500","bg-indigo-500","bg-pink-500"];
function avatarColor(id) {
  if (!id) return AVATAR_COLORS[0];
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

// ── Main component ────────────────────────────────────────────────────────────

const ProjectPipeline = () => {
  const [members, setMembers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [tooltip, setTooltip] = useState(null); // { task, x, y }
  const scrollRef = useRef(null);

  const totalDays = WEEKS * 7;
  const days = Array.from({ length: totalDays }, (_, i) => addDays(weekStart, i));
  const startStr = isoDate(weekStart);
  const endStr = isoDate(addDays(weekStart, totalDays - 1));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, tRes, pRes] = await Promise.all([
        api.get("/team/members").catch(() => ({ data: [] })),
        api.get("/tasks").catch(() => ({ data: [] })),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setMembers(mRes.data || []);
      setTasks(tRes.data || []);
      setProjects(pRes.data || []);
    } catch {
      toast.error("Failed to load pipeline data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Group tasks by assignee
  const tasksByMember = useCallback((userId) => {
    return tasks.filter((t) => {
      if (!t.due_date) return false;
      const assignees = t.assignees || (t.assignee ? [t.assignee] : []);
      return assignees.includes(userId);
    });
  }, [tasks]);

  // Project lookup
  const projectMap = Object.fromEntries(projects.map(p => [p.project_id, p]));

  // For a task, compute left/width as fractions of the visible range
  const barPosition = (task) => {
    const start = task.start_date ? new Date(task.start_date) : new Date(task.due_date);
    const end = new Date(task.due_date);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 0);

    const rangeStart = weekStart.getTime();
    const rangeEnd = addDays(weekStart, totalDays).getTime();

    if (end.getTime() < rangeStart || start.getTime() > rangeEnd) return null;

    const clampedStart = Math.max(start.getTime(), rangeStart);
    const clampedEnd = Math.min(end.getTime(), rangeEnd);

    const leftDays = (clampedStart - rangeStart) / (1000 * 60 * 60 * 24);
    const widthDays = Math.max((clampedEnd - clampedStart) / (1000 * 60 * 60 * 24), 1);

    return { left: leftDays * DAY_W, width: widthDays * DAY_W };
  };

  // Month groups for header
  const monthGroups = [];
  let cur = null;
  days.forEach((d, i) => {
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (key !== cur) {
      monthGroups.push({ month: MONTH_NAMES[d.getMonth()], year: d.getFullYear(), start: i, count: 1 });
      cur = key;
    } else {
      monthGroups[monthGroups.length - 1].count++;
    }
  });

  const today = isoDate(new Date());

  const goBack = () => setWeekStart(w => addDays(w, -7));
  const goForward = () => setWeekStart(w => addDays(w, 7));
  const goToday = () => setWeekStart(startOfWeek(new Date()));

  // Utilization: % of visible days with at least one task
  const utilization = (userId) => {
    const memberTasks = tasksByMember(userId);
    const busyDays = new Set();
    memberTasks.forEach(t => {
      const pos = barPosition(t);
      if (!pos) return;
      const startDay = Math.floor(pos.left / DAY_W);
      const endDay = Math.floor((pos.left + pos.width) / DAY_W);
      for (let d = startDay; d < endDay; d++) busyDays.add(d);
    });
    return Math.round((busyDays.size / totalDays) * 100);
  };

  return (
    <div className="flex flex-col h-full min-h-0" style={{ height: "calc(100vh - 56px)" }}>
      {/* ── Top bar ── */}
      <div className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between shrink-0">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-violet-500 mb-0.5">Delivery Engine</p>
          <h1 className="text-xl font-bold text-gray-900">Project Pipeline</h1>
          <p className="text-xs text-gray-400 mt-0.5">Team resource allocation across all active projects</p>
        </div>

        <div className="flex items-center gap-2">
          {/* Legend */}
          <div className="hidden lg:flex items-center gap-3 mr-4">
            {projects.slice(0, 5).map(p => {
              const col = colorForProject(p.project_id);
              return (
                <div key={p.project_id} className="flex items-center gap-1.5">
                  <div className={`w-2.5 h-2.5 rounded-full ${col.bg}`} />
                  <span className="text-xs text-gray-500 truncate max-w-[80px]">{p.name}</span>
                </div>
              );
            })}
          </div>

          {/* Navigation */}
          <button onClick={goBack} className="w-8 h-8 rounded-xl border border-gray-200 grid place-items-center text-gray-500 hover:bg-gray-50 hover:border-violet-300 hover:text-violet-600 transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={goToday} className="px-3 h-8 rounded-xl border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-violet-50 hover:border-violet-300 hover:text-violet-600 transition-colors">
            Today
          </button>
          <button onClick={goForward} className="w-8 h-8 rounded-xl border border-gray-200 grid place-items-center text-gray-500 hover:bg-gray-50 hover:border-violet-300 hover:text-violet-600 transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
          <button onClick={load} className="w-8 h-8 rounded-xl border border-gray-200 grid place-items-center text-gray-400 hover:bg-gray-50 transition-colors ml-1">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-sm text-gray-400 animate-pulse">Loading pipeline…</div>
        </div>
      ) : (
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* ── Left member panel (fixed) ── */}
          <div className="w-56 shrink-0 bg-white border-r border-gray-100 flex flex-col">
            {/* Column header */}
            <div className="h-16 border-b border-gray-100 flex items-end px-4 pb-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 flex items-center gap-1.5">
                <Users className="w-3 h-3" /> Team
              </span>
            </div>
            {/* Member rows */}
            <div className="flex-1 overflow-y-auto">
              {members.length === 0 ? (
                <div className="px-4 py-8 text-xs text-gray-400 text-center">No team members found</div>
              ) : members.map((m) => {
                const util = utilization(m.user_id);
                const initials = (m.name || m.email || "?").slice(0, 2).toUpperCase();
                return (
                  <div key={m.user_id} className="h-16 flex items-center gap-3 px-4 border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <div className={`w-8 h-8 rounded-xl ${avatarColor(m.user_id)} text-white text-xs font-bold grid place-items-center shrink-0 shadow-sm`}>
                      {m.picture
                        ? <img src={m.picture} alt={m.name} className="w-full h-full object-cover rounded-xl" />
                        : initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-gray-800 truncate">{m.name || m.email}</div>
                      <div className="text-[10px] text-gray-400 truncate capitalize">{m.role || "Member"}</div>
                      {/* Utilization bar */}
                      <div className="mt-1 flex items-center gap-1.5">
                        <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${util > 80 ? "bg-rose-400" : util > 50 ? "bg-amber-400" : "bg-emerald-400"}`}
                            style={{ width: `${util}%` }}
                          />
                        </div>
                        <span className="text-[9px] font-mono text-gray-400 shrink-0">{util}%</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Right timeline panel (scrollable) ── */}
          <div className="flex-1 min-w-0 flex flex-col overflow-hidden bg-gray-50">
            <div className="flex-1 overflow-auto" ref={scrollRef}>
              <div style={{ width: totalDays * DAY_W + "px", minWidth: "100%" }}>

                {/* ── Timeline header ── */}
                <div className="sticky top-0 z-20 bg-white border-b border-gray-100">
                  {/* Month row */}
                  <div className="flex h-7 border-b border-gray-50">
                    {monthGroups.map((mg, i) => (
                      <div
                        key={i}
                        className="flex items-center px-2 border-r border-gray-100 shrink-0"
                        style={{ width: mg.count * DAY_W }}
                      >
                        <span className="text-[10px] font-semibold text-gray-500">{mg.month} {mg.year}</span>
                      </div>
                    ))}
                  </div>
                  {/* Day row */}
                  <div className="flex h-9">
                    {days.map((d, i) => {
                      const ds = isoDate(d);
                      const isToday = ds === today;
                      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                      return (
                        <div
                          key={i}
                          className={`shrink-0 flex flex-col items-center justify-center border-r border-gray-100 ${isToday ? "bg-violet-50" : isWeekend ? "bg-gray-50/80" : ""}`}
                          style={{ width: DAY_W }}
                        >
                          <span className={`text-[9px] font-semibold uppercase ${isToday ? "text-violet-600" : "text-gray-400"}`}>
                            {DAY_NAMES[(d.getDay() + 6) % 7]}
                          </span>
                          <span className={`text-[10px] font-bold ${isToday ? "text-violet-700 bg-violet-500 text-white w-5 h-5 rounded-full flex items-center justify-center" : "text-gray-600"}`}>
                            {d.getDate()}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* ── Member rows ── */}
                {members.map((m) => {
                  const memberTasks = tasksByMember(m.user_id);
                  return (
                    <div
                      key={m.user_id}
                      className="relative h-16 border-b border-gray-100 hover:bg-white/60 transition-colors"
                    >
                      {/* Day grid lines */}
                      {days.map((d, i) => {
                        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                        const isToday = isoDate(d) === today;
                        return (
                          <div
                            key={i}
                            className={`absolute top-0 bottom-0 border-r border-gray-100 ${isWeekend ? "bg-gray-100/40" : ""} ${isToday ? "bg-violet-50/50" : ""}`}
                            style={{ left: i * DAY_W, width: DAY_W }}
                          />
                        );
                      })}

                      {/* Today marker */}
                      {(() => {
                        const todayIdx = days.findIndex(d => isoDate(d) === today);
                        if (todayIdx < 0) return null;
                        return (
                          <div
                            className="absolute top-0 bottom-0 w-0.5 bg-violet-400 z-10 pointer-events-none"
                            style={{ left: todayIdx * DAY_W + DAY_W / 2 }}
                          />
                        );
                      })()}

                      {/* Task bars — overlap-aware rows */}
                      {(() => {
                        // Sort tasks by start
                        const visible = memberTasks
                          .map(t => ({ ...t, pos: barPosition(t) }))
                          .filter(t => t.pos !== null)
                          .sort((a, b) => a.pos.left - b.pos.left);

                        // Pack into lanes (avoid overlap)
                        const lanes = [];
                        visible.forEach(t => {
                          let placed = false;
                          for (let lane = 0; lane < lanes.length; lane++) {
                            const lastInLane = lanes[lane][lanes[lane].length - 1];
                            if (lastInLane.pos.left + lastInLane.pos.width + 2 <= t.pos.left) {
                              lanes[lane].push(t);
                              placed = true;
                              break;
                            }
                          }
                          if (!placed) lanes.push([t]);
                        });

                        const laneHeight = 12;
                        const laneGap = 3;
                        const topOffset = 8;

                        return lanes.map((lane, li) =>
                          lane.map((t) => {
                            const proj = projectMap[t.project_id];
                            const col = colorForProject(t.project_id);
                            const label = `${proj?.name || "Task"} · ${t.title || t.name || ""}`;
                            const top = topOffset + li * (laneHeight + laneGap);

                            return (
                              <div
                                key={t.task_id}
                                className={`absolute rounded-full ${col.bg} ${col.text} cursor-pointer hover:brightness-90 transition-all z-10 flex items-center px-2 overflow-hidden`}
                                style={{
                                  left: t.pos.left + 2,
                                  width: Math.max(t.pos.width - 4, 8),
                                  top,
                                  height: laneHeight,
                                }}
                                onMouseEnter={(e) => setTooltip({
                                  task: t,
                                  proj,
                                  col,
                                  x: e.clientX,
                                  y: e.clientY,
                                })}
                                onMouseLeave={() => setTooltip(null)}
                              >
                                <span className="text-[8px] font-semibold truncate leading-none">{label}</span>
                              </div>
                            );
                          })
                        );
                      })()}
                    </div>
                  );
                })}

                {members.length === 0 && (
                  <div className="flex items-center justify-center py-20 text-sm text-gray-400">
                    No team members — invite your team to see allocations here.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Tooltip ── */}
      {tooltip && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{ left: tooltip.x + 12, top: tooltip.y - 10 }}
        >
          <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-3 min-w-[180px] max-w-[240px]">
            <div className={`inline-block text-[9px] font-bold px-2 py-0.5 rounded-full mb-2 ${tooltip.col.bg} text-white`}>
              {tooltip.proj?.name || "Project"}
            </div>
            <div className="text-xs font-semibold text-gray-800 leading-snug mb-1">{tooltip.task.title || tooltip.task.name}</div>
            {tooltip.task.due_date && (
              <div className="text-[10px] text-gray-400 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Due {new Date(tooltip.task.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </div>
            )}
            {tooltip.task.status && (
              <div className="text-[10px] text-gray-400 mt-0.5 capitalize">Status: {tooltip.task.status}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectPipeline;
